// Modules
import { useState, useEffect } from "react"
import moment from "moment"
import { toast } from "react-toastify"

// Components
import { Card } from "~components/Card"
import PageContent from "~components/PageContent"
import CompanyModal from "~components/companies/CompanyModal"
import CompaniesList from "~components/companies/CompaniesList"
import TablePaginator from "~components/TablePaginator"
import TableSearchField from "~components/TableSearchField"
import { ContainerSpaceBetween } from "~styled/containers"
import { ButtonPrimarySoftLarge } from "~styled/button"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"

// Services
import CompanyService from "~services/CompanyService"

// Shapes
import { OnRequestCloseProps } from "~shapes/modal"
import { paginatorInitState, TablePaginatorShape } from "~shapes/api-table"
import {
  CompanyShape,
  CompaniesListShape,
  GetCompanyOptionsShape,
} from "~shapes/company"
import PageHeader from "~components/PageHeader"

const companies = () => {
  const [companies, setCompanies] = useState<CompanyShape[]>([])
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const [paginator, setPaginator] = useState<TablePaginatorShape>(
    paginatorInitState
  )
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [fetchOptions, setFetchOptions] = useState<
    GetCompanyOptionsShape | undefined
  >(undefined)
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false)

  const tableOptions = { sort: "name", size: 15 }

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = (options?: GetCompanyOptionsShape) => {
    setFetchOptions(options)
    setIsTableLoading(true)

    CompanyService.getCompaniesWithLocation({ ...tableOptions, ...options })
      .then((res: CompaniesListShape) => {
        setPaginator(res.page)
        setCompanies(res._embedded.companies)
      })
      .catch(() => {
        setPaginator(null!)
        setCompanies([])
      })
      .finally(() => {
        setIsTableLoading(false)
      })
  }

  const handlePageChange = (page: number) => {
    fetchCompanies({ page })
  }

  const handleEditCompany = (id: string) => {
    setIsModalOpen(true)
    setCompanyId(id)
  }

  const handleOpenCompanyModal = () => {
    setCompanyId(undefined)
    setIsModalOpen(true)
  }

  const handleOnRequestClose = ({ shouldRefreshList }: OnRequestCloseProps) => {
    if (shouldRefreshList)
      fetchCompanies({
        ...fetchOptions,
        page: paginator.number,
      })
    setIsModalOpen(false)
  }

  const handleCompanyStatusChange = (id: string, status: string) => {
    CompanyService.patchCompany({ id, status })
      .then(response => {
        const updatedCompanies = companies.map(company => {
          if (company.id !== response.id) return company
          return { ...company, status: response.status }
        })

        setCompanies(updatedCompanies)
      })
      .catch(() => {
        toast.error(`Failed to ${status.toLowerCase()} the company`)
      })
  }

  return (
    <PageContent>
      <PageHeader title={"Companies"} />
      <Card>
        <ContainerSpaceBetween style={{ marginBottom: 30 }}>
          <TableSearchField
            searchBy={["name"]}
            placeholder="SEARCH BY COMPANY NAME"
            callback={fetchCompanies}
          />

          <ButtonPrimarySoftLarge onClick={handleOpenCompanyModal}>
            Create a new company
          </ButtonPrimarySoftLarge>
        </ContainerSpaceBetween>

        <CompaniesList
          companies={companies}
          isLoading={isTableLoading}
          onChangeStatus={handleCompanyStatusChange}
          onClickCompanyEdit={handleEditCompany}
        />
        {paginator && (
          <TablePaginator page={paginator} onPageChange={handlePageChange} />
        )}
      </Card>

      {isModalOpen && (
        <IsAuthorized action={RBAC_RULES.CREATE_COMPANY}>
          <CompanyModal
            companyId={companyId}
            onRequestClose={handleOnRequestClose}
          />
        </IsAuthorized>
      )}
    </PageContent>
  )
}

export default companies
